// @flow

import * as React from 'react';
import qs from 'qs';
import {ButtonLink} from 'sail/Button';
import {getRootPathForArticle, createStripeInternalUrl} from 'docs/util';
import type {Account, Article} from 'docs/data';
import {useAnalytics} from 'docs/data';
import {XRaySidebarSection} from './XRaySidebarSection';

const getJiraUrl = (article: Article) => {
  const options = {
    pid: 10136, // DOCS Project
    issuetype: 10102, // Bug
    components: 10275, // Ask
    priority: 3, // Medium
    description: [
      `Reporting a bug on page: ${article.path}\n`,
      `-----\n`,
      `(Please delete the following text and describe the issue!)\n\n`,
      `Ask the Docs team about anything related to stripe.com/docs. Make sure to provide the URL`,
      `of any docs that your request relates to. If you're requesting changes to product documentation,`,
      `confirm with the responsible team first and link to the Jira/Slack/email discussion.`,
    ].join(''),
  };

  const query = qs.stringify(options);
  return `https://jira.corp.stripe.com/secure/CreateIssueDetails!init.jspa?${query}`;
};

const getGitHubUrl = (article: Article, isEditLink?: boolean = false) => {
  if (article.xray) {
    const urlType = isEditLink ? 'github_edit' : 'github';
    const {filename} = article.xray;
    const root = getRootPathForArticle(article);
    return createStripeInternalUrl(urlType, {
      root,
      filename,
    });
  }
};

const getMetricsUrl = (article: Article) => {
  if (article.path) {
    return createStripeInternalUrl('docs_stats', {path: article.path});
  }
};

const getVisualStudioCodeUrl = (account: Account, article: Article) => {
  if (account.xray && article.xray) {
    const {username} = account.xray;
    const {filename} = article.xray;
    const root = getRootPathForArticle(article);

    return createStripeInternalUrl('vscode', {
      username,
      root,
      filename,
    });
  }
};

type ActionsProps = {|
  account: Account,
  article: Article,
|};

export const Actions = ({account, article}: ActionsProps) => {
  const {trackAction} = useAnalytics();

  if (!account.xray) {
    console.warn('Missing necessary x-ray data. Please contact #docs-product!'); // eslint-disable-line no-console
    return null;
  }

  return (
    <XRaySidebarSection title="Actions">
      <ButtonLink
        label="View history"
        href={getGitHubUrl(article)}
        icon="external"
        iconPosition="right"
        target="_blank"
        onClick={() => {
          trackAction('docs_xray', {type: 'view_history'});
        }}
      />
      <ButtonLink
        label="Edit in VSCode"
        href={getVisualStudioCodeUrl(account, article)}
        icon="external"
        iconPosition="right"
        target="_blank"
        onClick={() => {
          trackAction('docs_xray', {type: 'edit_in_vscode'});
        }}
      />
      <ButtonLink
        label="Edit in GitHub"
        href={getGitHubUrl(article, true)}
        icon="external"
        iconPosition="right"
        target="_blank"
        onClick={() => {
          trackAction('docs_xray', {type: 'edit_in_github'});
        }}
      />
      <ButtonLink
        label="File a bug"
        href={getJiraUrl(article)}
        icon="external"
        iconPosition="right"
        target="_blank"
        onClick={() => {
          trackAction('docs_xray', {type: 'file_bug'});
        }}
      />
      <ButtonLink
        label="Metrics"
        href={getMetricsUrl(article)}
        icon="external"
        iconPosition="right"
        target="_blank"
        onClick={() => {
          trackAction('docs_xray', {type: 'docs_stats'});
        }}
      />
    </XRaySidebarSection>
  );
};
